import { currentVisitorId } from './visitor';
import { isTestVisit } from './testVisit';

/**
 * V7's A/B test (docs/superpowers/specs/2026-10-16-v7-build.md), replacing V6's `demo_question`.
 * Both groups go demo → "What is it?" (/new/what) → questions → build:
 *   a: ONE screen of questions for the type, then the build starts by itself (no plan screens);
 *   b: the questions one per screen, then the plan's own screens (features, look, review) and Build.
 *
 * A visitor joins when they first see a demo (an ad visit): `variant()` puts them in a group by a
 * hash of their visitor id (analytics/visitor.ts), half and half, and keeps it in localStorage so a
 * later visit is in the same group even if the id were lost. Somebody with no demo never joins, and
 * their events carry no variant. A test visit (`test=1`) may choose its group with `?variant=a|b`,
 * for the rest of the tab; a real visitor's address cannot change theirs.
 *
 * Every funnel event a member sends carries `variant` (analytics/funnel.ts `assignedVariants`), to our
 * own ingest only, so the two groups can be counted apart from the first step to the last.
 *
 * And `trial_card` (owner, 2026-11-01: "run it now, 50/50"): whether the 14-day trial takes the card
 * up front (a, as since 2026-10-01) or starts with none and asks for it before the trial ends (b).
 * Joined at the plan choice; its events carry `card: 'now' | 'later'`. A test visit chooses with
 * `?trialcard=a|b`. Its own hash, so the two tests split independently of each other.
 *
 * OFF (owner, later on 2026-11-01, before it ever shipped: "card for everyone"): nobody joins, every trial
 * takes the card, and the server ignores a request for the other (stripe.ts, `NO_CARD_TRIAL`). Turning it
 * on is this flag and that variable together.
 */
const NO_CARD_TEST_ON = false;
let noCardTestOn = NO_CARD_TEST_ON;
export type Experiment = 'v7_flow' | 'trial_card';
export type Variant = 'a' | 'b';

const key = (name: Experiment): string => `saddle.exp.${name}`;
const isVariant = (v: unknown): v is Variant => v === 'a' || v === 'b';

/** This page's memory, for a browser whose storage refuses: the group holds for the page load. */
const heldHere = new Map<Experiment, Variant>();

/*
 * FNV-1a over the id with the experiment's name, so a later experiment splits people differently.
 * Its lowest bit is the group: even 'a', odd 'b'.
 */
export function variantForVisitor(visitorId: string, name: Experiment = 'v7_flow'): Variant {
  const text = `${name}:${visitorId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Fold the high half in: FNV's lowest bit alone follows the last character too closely.
  h ^= h >>> 16;
  return (h & 1) === 0 ? 'a' : 'b';
}

/** A test visit's chosen group: from the address, then from this tab's memory of it. */
const TEST_PARAM: Record<Experiment, string> = { v7_flow: 'variant', trial_card: 'trialcard' };

function testOverride(name: Experiment): Variant | null {
  if (!isTestVisit()) return null;
  try {
    const asked = new URLSearchParams(typeof location !== 'undefined' ? location.search : '').get(TEST_PARAM[name]);
    if (isVariant(asked)) {
      sessionStorage.setItem(key(name), asked);
      return asked;
    }
    const kept = sessionStorage.getItem(key(name));
    return isVariant(kept) ? kept : null;
  } catch {
    return null;
  }
}

/** The group this visitor is in, or null when they have not joined. Never puts them in one. */
export function assignedVariant(name: Experiment): Variant | null {
  const forced = testOverride(name);
  if (forced) return forced;
  try {
    const kept = localStorage.getItem(key(name));
    if (isVariant(kept)) return kept;
  } catch {
    /* fall through to this page's memory */
  }
  return heldHere.get(name) ?? null;
}

/** The group this visitor is in, joining them to it now if they were not. */
export function variant(name: Experiment): Variant {
  const known = assignedVariant(name);
  if (known) return known;
  const chosen = variantForVisitor(currentVisitorId(), name);
  heldHere.set(name, chosen);
  try {
    localStorage.setItem(key(name), chosen);
  } catch {
    /* held for this page load only */
  }
  return chosen;
}

/** What every funnel event carries for the experiments this visitor is in (one, today). */
export function assignedVariants(): { variant?: Variant; card?: 'now' | 'later' } {
  const v = assignedVariant('v7_flow');
  const c = noCardTestOn ? assignedVariant('trial_card') : null;
  return { ...(v ? { variant: v } : {}), ...(c ? { card: c === 'b' ? 'later' as const : 'now' as const } : {}) };
}

/** The trial this visitor is offered: the card up front, or none until before it ends. Joins them. */
export function trialCard(): 'now' | 'later' {
  if (!noCardTestOn) return 'now';
  return variant('trial_card') === 'b' ? 'later' : 'now';
}

/** Whether this visitor's trial starts without a card: in the test, and in its "later" half. Never joins. */
export function cardLater(): boolean {
  return noCardTestOn && assignedVariant('trial_card') === 'b';
}

/** For tests of the no-card half while the test is off. */
export function _setNoCardTestForTest(on: boolean): void {
  noCardTestOn = on;
}

/** Test-only: forget this page's memory of any group. */
export function _resetExperimentsForTest(): void {
  heldHere.clear();
  noCardTestOn = NO_CARD_TEST_ON;
}
